import { useEffect, useRef, useState } from 'react';

type LatLng = { lat: number; lng: number };

type Props = {
  pickup: LatLng;
  /** Optional. When omitted, the map renders only the pickup pin (no route
   *  line, no OSRM call) and centers on the pickup. */
  drop?: LatLng | null;
  className?: string;
  height?: number;
};

/**
 * Lazy-loaded Leaflet map with OpenStreetMap tiles and an OSRM-resolved road line.
 *
 * With both pickup + drop: pickup ring + drop square + OSRM road line (dashed
 * straight line fallback while loading or on failure), bounds fitted to both.
 * With only pickup: just the pickup pin, centered with a single-point zoom.
 *
 * This is a static overview of the booking route only (no turn-by-turn
 * navigation — the in-app "Open in Maps" deep link was removed).
 *
 * Important: the `setTimeout` invalidateSize / fitBounds is INTENTIONAL.
 * Leaflet needs a layout pass to size correctly inside flexbox parents; without
 * it the map renders blank. See driver-app v2 learnings doc.
 *
 * Scroll-wheel zoom is disabled because the driver app is mobile-first and the
 * map sits in a scrollable page — pinch + tap zoom controls work via touch.
 */
export function MapHero({ pickup, drop, className, height = 280 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let map: any;

    (async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css' as any);
      if (cancelled || !containerRef.current) return;

      // attributionControl stays ON. It was off, which meant the attribution
      // string set on the tile layer below never rendered anywhere: OpenStreetMap's
      // tile policy expects visible credit, and the ops and portal maps show it.
      map = L.map(containerRef.current, {
        zoomControl: false,
        scrollWheelZoom: false,
      });

      // OpenStreetMap's own tiles, no key: CARTO's basemaps carry an "API KEY
      // REQUIRED" watermark since 2026-09-04. Same switch as the ops and portal maps.
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      L.circleMarker([pickup.lat, pickup.lng], {
        radius: 6, color: '#1B6A41', fillColor: '#278E5E', fillOpacity: 1, weight: 2,
      }).addTo(map);

      // Pickup-only branch: center on the pickup pin, skip the line + OSRM call.
      if (!drop) {
        map.setView([pickup.lat, pickup.lng], 15);
        setTimeout(() => {
          if (cancelled) return;
          map.invalidateSize();
          map.setView([pickup.lat, pickup.lng], 15);
        }, 100);
        setReady(true);
        return;
      }

      L.circleMarker([drop.lat, drop.lng], {
        radius: 6, color: '#1f2937', fillColor: '#1f2937', fillOpacity: 1, weight: 0,
      }).addTo(map);

      const fallbackLine = L.polyline(
        [[pickup.lat, pickup.lng], [drop.lat, drop.lng]],
        { color: '#1B6A41', weight: 3, opacity: 0.45, dashArray: '6 6' },
      ).addTo(map);

      map.fitBounds(L.latLngBounds([pickup, drop]).pad(0.25));
      setTimeout(() => {
        if (cancelled) return;
        map.invalidateSize();
        map.fitBounds(L.latLngBounds([pickup, drop]).pad(0.25));
      }, 100);
      setReady(true);

      // OSRM road geometry — best-effort; replaces dashed fallback on success.
      try {
        const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${drop.lng},${drop.lat}?overview=full&geometries=geojson`;
        const res = await fetch(osrmUrl);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const coords = data?.routes?.[0]?.geometry?.coordinates as [number, number][] | undefined;
        if (!coords || cancelled) return;
        fallbackLine.remove();
        L.polyline(coords.map(([lng, lat]) => [lat, lng]), { color: '#1B6A41', weight: 4, opacity: 0.85 }).addTo(map);
      } catch {
        /* network failure — fallback line is already drawn */
      }
    })();

    return () => {
      cancelled = true;
      if (map) map.remove();
    };
  }, [pickup.lat, pickup.lng, drop?.lat, drop?.lng]);

  return (
    <div className={className} style={{ position: 'relative', isolation: 'isolate', width: '100%', height, borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {!ready ? <div style={{ position: 'absolute', inset: 0, background: 'var(--mint-soft)' }} /> : null}
    </div>
  );
}
